import React, { useEffect, useState } from 'react';
import { FC } from 'react';
import { GetSelectivityTemplateDetailsRequest, OlmSelectivityProfile } from '@/api/client';
import { GridCellProps, GridDetailRowProps } from '@progress/kendo-react-grid';
import SelectivyDetails from '@/components/message-details/selectivity/SelectivityDetails';
import ErrorBoundary from '@/presentation/errorboundary';
import Text from '@/presentation/text';
import Api from '@/api/helpers';
import { useAppDispatch, useAppSelector } from '@/hooks/customHooks';
import { setExpandedTemplateDetails, setExpandTemplate } from '@/features/selectivitySlice';
import styled from 'styled-components';
import { SLoader } from '@/presentation/loader/Loader.styled';
import HistoryGrid from '../message-details/history-item/HistoryGrid';

const SGridTemplateDetailRow = styled.div`
  position: relative;

  > div > div {
    min-width: 800px;
    width: 65vw;
  }
`;

const GridTemplateDetailRow: FC<GridDetailRowProps> = (props) => {
  const { dataItem } = props;
  const [isLoadingDetails, setIsLoadingDetails] = useState(true);
  const activeDocType = useAppSelector((state) => state.docTypes.activeDocType);
  const activeCustomer = useAppSelector((state) => state.customers.activeCustomer);
  const { expandedTemplateDetails } = useAppSelector((state) => state.selectivity);
  const dispatch = useAppDispatch();
  const TIME_TO_CACHE_TEMPLATE_IN_MILS = 3600000; // 1 hour

  const fetchTemplate = async () => {
    const client = Api.getSelectivityClient(dispatch);
    setIsLoadingDetails(true);
    try {
      if (activeCustomer && activeDocType && activeDocType.primaryKey) {
        const response = await client.getSelectivityTemplateDetails(
          new GetSelectivityTemplateDetailsRequest({
            selectivityTemplateId: parseInt(dataItem.primaryKey),
            docTypeId: activeDocType?.primaryKey,
            sysPrin: activeCustomer?.sysPrin,
          }),
        );
        dispatch(setExpandedTemplateDetails(response));
      }
    } catch (err) {
      dispatch(setExpandTemplate(props as GridCellProps));
    } finally {
      setIsLoadingDetails(false);
    }
  };

  useEffect(() => {
    if (
      expandedTemplateDetails &&
      expandedTemplateDetails[dataItem.primaryKey] &&
      Date.now() - expandedTemplateDetails[dataItem.primaryKey].timeSet <= TIME_TO_CACHE_TEMPLATE_IN_MILS
    ) {
      setIsLoadingDetails(false);
    } else {
      fetchTemplate();
    }
  }, []);

  const renderSelectivityDetails = (selectivityProfile: OlmSelectivityProfile | undefined): React.ReactNode => {
    if (selectivityProfile) {
      return (
        <div>
          <SelectivyDetails selectivityProfile={selectivityProfile} />
        </div>
      );
    }

    return null;
  };

  const renderTemplateDetails = () => {
    if (expandedTemplateDetails && expandedTemplateDetails[dataItem.primaryKey]) {
      const templateDetails = expandedTemplateDetails[dataItem.primaryKey];
      return (
        <ErrorBoundary size="m">
          <div className="mb-20">
            <div>
              <Text style={{ paddingBottom: '1em' }} size="xs">
                Profile Template
              </Text>
            </div>
            {renderSelectivityDetails(templateDetails.profile as unknown as OlmSelectivityProfile)}
            <Text style={{ paddingTop: '2em', paddingBottom: '1em' }} size="xs">
              History
            </Text>
            <HistoryGrid messageHistory={templateDetails.history} />
          </div>
        </ErrorBoundary>
      );
    }
  };

  return (
    <SGridTemplateDetailRow>
      {isLoadingDetails ? <SLoader style={{ marginTop: '15px' }} /> : renderTemplateDetails()}
    </SGridTemplateDetailRow>
  );
};

export default GridTemplateDetailRow;
